class LocationSelectorPopup {
  constructor() {
    this.popup = document.getElementById('cs-location-selector-popup');
    this.overlay = this.popup?.querySelector('.cs-location-selector-popup__overlay');
    this.closeButtons = this.popup?.querySelectorAll('.cs-location-selector-popup__close');
    this.triggers = document.querySelectorAll('.js-location-selector-trigger');
    this.form = this.popup?.querySelector('#cs-location-selector-form');

    if (!this.popup) return;

    this.init();
  }

  /**
   * Lee las traducciones inyectadas desde Liquid (locale keys) para textos
   * que se construyen dinámicamente en JS. Si el script no está presente,
   * devuelve un objeto vacío y cada caller usa su propio texto en español
   * como fallback, para no romper nada.
   * @returns {object}
   */
  getTranslations() {
    if (this._translations) return this._translations;
    try {
      const script = document.getElementById('location-selector-translations');
      this._translations = script ? JSON.parse(script.textContent) : {};
    } catch (error) {
      this._translations = {};
    }
    return this._translations;
  }

  /**
   * Función helper para loggear cart attributes de forma consistente
   * @param {string} context - Contexto del log (ej: "Al cargar", "Después de actualizar")
   */
  async debugCartAttributes(context) {
    try {
      const cartResponse = await fetch('/cart.js');
      if (cartResponse.ok) {
        const cart = await cartResponse.json();
        const timestamp = new Date().toISOString();
        
        console.group(`📦 ${context}`);
        console.log('⏰ Timestamp:', timestamp);
        console.log('🛒 Cart ID:', cart.token || 'N/A');
        console.log('📊 Total de atributos:', cart.attributes ? Object.keys(cart.attributes).length : 0);
        
        if (cart.attributes && Object.keys(cart.attributes).length > 0) {
          console.log('📋 Todos los cart attributes:');
          console.table(cart.attributes);
          
          // Resaltar atributos de pickup
          const pickupAttributes = {};
          Object.keys(cart.attributes).forEach(key => {
            if (key.startsWith('pickup_') || key === 'branch_filter_selection') {
              pickupAttributes[key] = cart.attributes[key];
            }
          });
          
          if (Object.keys(pickupAttributes).length > 0) {
            console.log('🎯 Atributos de pickup:');
            console.table(pickupAttributes);
          } else {
            console.warn('⚠️ No se encontraron atributos de pickup');
          }
        } else {
          console.warn('⚠️ El carrito no tiene atributos');
        }
        console.groupEnd();
      } else {
        console.error('❌ Error al obtener cart attributes:', cartResponse.statusText);
      }
    } catch (error) {
      console.error('❌ Error al debuggear cart attributes:', error);
    }
  }

  init() {
    this.attachEventListeners();
    this.initCustomSelect();
    this.initDateTime();
    this.initRadioButtons();
    this.initDepartmentFromStorage();
    this.initTerminalFromStorage();
    this.initDateTimeFromStorage();
    this.autoOpenOnHomepage();
  }

  autoOpenOnHomepage() {
    const path = window.location.pathname;
    const isHomepage = path === '/' || path === '' || /^\/[a-z]{2}\/?$/.test(path);
    if (!isHomepage) return;

    const hasLocation = !!localStorage.getItem('locationData');
    if (hasLocation) return;

    setTimeout(() => this.open(), 800);
  }

  initCustomSelect() {
    const selectWrapper = this.popup?.querySelector('.cs-location-selector-popup__select-wrapper');
    const selectTrigger = selectWrapper?.querySelector('.cs-location-selector-popup__select-trigger');
    const selectOptions = selectWrapper?.querySelectorAll('.cs-location-selector-popup__select-option');
    const hiddenInput = selectWrapper?.querySelector('input[type="hidden"]');

    if (!selectWrapper || !selectTrigger) return;

    selectTrigger.addEventListener('click', (e) => {
      e.stopPropagation();
      const isOpen = selectWrapper.classList.contains('is-open');

      // Cerrar otros selects
      document.querySelectorAll('.cs-location-selector-popup__select-wrapper.is-open').forEach((wrapper) => {
        if (wrapper !== selectWrapper) {
          wrapper.classList.remove('is-open');
        }
      });

      selectWrapper.classList.toggle('is-open', !isOpen);
    });

    // Cerrar al hacer click fuera
    document.addEventListener('click', (e) => {
      if (!selectWrapper.contains(e.target)) {
        selectWrapper.classList.remove('is-open');
      }
    });

    // Seleccionar opción
    if (selectOptions) {
      selectOptions.forEach((option) => {
        option.addEventListener('click', (e) => {
          e.stopPropagation();
          const value = option.getAttribute('data-value');
          const label = option.querySelector('.cs-location-selector-popup__option-label')?.textContent;
          const flagIcon = option.querySelector('.cs-location-selector-popup__option-flag')?.innerHTML;

          // Actualizar trigger
          const selectValue = selectTrigger.querySelector('.cs-location-selector-popup__select-value');
          if (selectValue) {
            selectValue.textContent = label;
          }

          // Actualizar input hidden
          if (hiddenInput) {
            hiddenInput.value = value;
          }

          // Actualizar ícono de bandera en el trigger
          const triggerFlagIcon = selectTrigger.querySelector('.cs-location-selector-popup__flag-icon');
          if (triggerFlagIcon && flagIcon) {
            triggerFlagIcon.innerHTML = flagIcon;
          }

          selectWrapper.classList.remove('is-open');
        });
      });
    }
  }

  /**
   * Obtiene la hora actual en el huso horario de Ecuador (UTC-5),
   * normalizando la hora del navegador a la zona horaria de la tienda.
   */
  getEcuadorNow() {
    const now = new Date();
    const utcTime = now.getTime() + now.getTimezoneOffset() * 60000;
    const ecuadorOffsetMinutes = -5 * 60; // UTC-5
    return new Date(utcTime + ecuadorOffsetMinutes * 60000);
  }

  /**
   * Primer slot de 30 min seleccionable hoy (30 min de antelacion), en minutos
   * desde medianoche. Devuelve 1440 si el proximo slot ya cae en el dia
   * siguiente: con horario de recogida 24h no queda cupo para hoy.
   */
  getMinSlotMinutesToday(now) {
    const minTime = new Date(now.getTime() + 30 * 60 * 1000);
    minTime.setSeconds(0, 0);

    let snapH = minTime.getHours();
    let snapM = minTime.getMinutes();
    if (snapM > 0 && snapM <= 30) { snapM = 30; }
    else if (snapM > 30) { snapM = 0; snapH++; }

    if (minTime.getDate() !== now.getDate() || snapH > 23) return 24 * 60;
    return snapH * 60 + snapM;
  }

  initDateTime() {
    // Buscar elementos dentro del popup específicamente
    const dateElement = this.popup?.querySelector('#pickup-date') || document.getElementById('pickup-date');
    const timeElement = this.popup?.querySelector('#pickup-time') || document.getElementById('pickup-time');
    const dateInput = this.popup?.querySelector('#pickup-date-input') || document.getElementById('pickup-date-input');
    const timeInput = this.popup?.querySelector('#pickup-time-input') || document.getElementById('pickup-time-input');

    // Establecer fecha mínima (hoy) y hora mínima (30 minutos desde ahora) en horario de Panamá
    const now = this.getEcuadorNow();
    const minDate = new Date(now);
    minDate.setHours(0, 0, 0, 0); // Inicio del día actual

    // Establecer atributos min en los inputs
    if (dateInput) {
      const minDateString = minDate.toISOString().split('T')[0]; // YYYY-MM-DD
      dateInput.setAttribute('min', minDateString);

      const maxDate = new Date(now);
      maxDate.setHours(0, 0, 0, 0);
      maxDate.setDate(maxDate.getDate() + 15);
      dateInput.setAttribute('max', maxDate.toISOString().split('T')[0]);
    }

    if (timeInput) {
      // Deshabilitar opciones pasadas en el select (30 min de antelación)
      const minTotalMinutes = this.getMinSlotMinutesToday(now);
      Array.from(timeInput.options || []).forEach(option => {
        if (!option.value) return; // opción placeholder en blanco: mantiene su propio disabled
        const [oh, om] = option.value.split(':').map(Number);
        option.disabled = oh * 60 + om < minTotalMinutes;
      });
    }

    // No se inicializa fecha/hora por defecto: el usuario debe seleccionarlas explícitamente

    // Actualizar min de hora cuando cambie la fecha
    if (dateInput && timeInput) {
      dateInput.addEventListener('change', () => {
        this.updateTimeMin(dateInput.value, timeInput);
      });
    }

    // Actualizar display cuando cambie la fecha
    if (dateInput) {
      const handleDateChange = (e) => {
        const value = e.target.value;
        if (value) {
          this.updateDateDisplay(value);
          this.setFieldError('date', null);
        }
      };

      dateInput.addEventListener('change', handleDateChange);

      // También escuchar input para actualización inmediata
      dateInput.addEventListener('input', handleDateChange);
    }

    // Actualizar display cuando cambie la hora, y corregir al rango/steps válidos
    if (timeInput) {
      const handleTimeChange = (e) => {
        const value = e.target.value;
        if (value) {
          this.updateTimeDisplay(value);
          this.setFieldError('time', null);
        }
      };

      timeInput.addEventListener('change', handleTimeChange);

      // También escuchar input para actualización inmediata
      timeInput.addEventListener('input', handleTimeChange);
    }

    // El input tiene pointer-events: none, así que los clicks pasan al label
    // El label tiene for="pickup-date-input" y for="pickup-time-input",
    // lo que debería activar el input automáticamente
    // Solo necesitamos asegurar que el picker se abra cuando el input recibe focus
    if (dateInput) {
      dateInput.addEventListener(
        'click',
        () => {
          if (typeof dateInput.showPicker === 'function') {
            try {
              dateInput.showPicker();
            } catch (error) {
              // El navegador abre el picker nativamente
            }
          }
        },
        { passive: true },
      );
    }

    if (timeInput) {
      const timeField = timeInput.closest('.cs-location-selector-popup__time-field');
      if (timeField) {
        // Cuando se hace click en el label, el atributo 'for' activa el input
        // Agregar listener para abrir el picker cuando el input recibe focus desde el label
        timeField.addEventListener(
          'click',
          (e) => {
            // No forzar showPicker en <select>: el navegador lo maneja nativamente
            // y llamarlo tras seleccionar una opción causa que el dropdown reaparezca
            if (timeInput.tagName === 'SELECT') return;
            setTimeout(() => {
              if (document.activeElement === timeInput) {
                if (timeInput.showPicker && typeof timeInput.showPicker === 'function') {
                  try {
                    timeInput.showPicker();
                  } catch (error) {
                    console.log('showPicker no disponible:', error);
                  }
                }
              }
            }, 10);
          },
          { passive: true },
        );
      }
    }
  }

  /**
   * Inicializa fecha y hora desde localStorage si existen
   * Valida que las fechas no sean en el pasado
   */
  initDateTimeFromStorage() {
    try {
      const stored = localStorage.getItem('locationData');
      const locationData = stored ? JSON.parse(stored) : null;

      if (locationData) {
        // Buscar elementos dentro del popup específicamente
        const dateInput =
          this.popup?.querySelector('#pickup-date-input') || document.getElementById('pickup-date-input');
        const timeInput =
          this.popup?.querySelector('#pickup-time-input') || document.getElementById('pickup-time-input');
        const dateElement = this.popup?.querySelector('#pickup-date') || document.getElementById('pickup-date');
        const timeElement = this.popup?.querySelector('#pickup-time') || document.getElementById('pickup-time');

        // Validar fecha y hora antes de cargarlas
        const isValid = this.validateDateTime(locationData.date, locationData.time);

        // Cargar fecha desde localStorage (solo si es válida)
        if (locationData.date && dateInput) {
          if (isValid) {
            dateInput.value = locationData.date;
            if (dateElement) {
              // Si hay dateDisplay, usarlo, sino calcular desde date
              if (locationData.dateDisplay) {
                dateElement.textContent = this.formatDateForLocale(locationData.date) || locationData.dateDisplay;
              } else {
                this.updateDateDisplay(locationData.date);
              }
            }
          } else {
            // Si la fecha es inválida, limpiar el campo para forzar al usuario a elegir
            dateInput.value = '';
          }
        }

        // Cargar hora desde localStorage (solo si es válida)
        if (locationData.time && timeInput) {
          if (isValid) {
            timeInput.value = locationData.time;
            if (timeElement) {
              // Si hay timeDisplay, usarlo, sino calcular desde time
              if (locationData.timeDisplay) {
                timeElement.textContent = locationData.timeDisplay;
              } else {
                this.updateTimeDisplay(locationData.time);
              }
            }
          } else {
            // Si la hora es inválida, limpiar para forzar al usuario a elegir
            timeInput.value = '';
          }
        }

        // Actualizar min de hora basado en la fecha cargada
        if (dateInput && timeInput && dateInput.value) {
          this.updateTimeMin(dateInput.value, timeInput);
        }
      }
    } catch (error) {
      console.error('Error al cargar fecha/hora desde localStorage:', error);
    }
  }

  updateDateDisplay(dateValue) {
    // Buscar el elemento dentro del popup específicamente
    const dateElement = this.popup?.querySelector('#pickup-date') || document.getElementById('pickup-date');
    if (!dateElement || !dateValue) {
      console.warn('updateDateDisplay: dateElement o dateValue no encontrado', { dateElement, dateValue });
      return;
    }

    const displayText = this.formatDateForLocale(dateValue) || this.formatDateDisplay(dateValue);
    if (displayText) {
      dateElement.textContent = displayText;
      console.log('updateDateDisplay: actualizado a', displayText, 'en elemento:', dateElement);
    }
  }

  updateTimeDisplay(timeValue) {
    // Buscar el elemento dentro del popup específicamente
    const timeElement = this.popup?.querySelector('#pickup-time') || document.getElementById('pickup-time');
    if (!timeElement || !timeValue) {
      console.warn('updateTimeDisplay: timeElement o timeValue no encontrado', { timeElement, timeValue });
      return;
    }

    const displayText = this.formatTimeDisplay(timeValue);
    if (displayText) {
      timeElement.textContent = displayText;
      console.log('updateTimeDisplay: actualizado a', displayText, 'en elemento:', timeElement);
    }
  }

  /**
   * Actualiza el atributo min del input de hora basado en la fecha seleccionada
   * @param {string} dateValue - Fecha en formato YYYY-MM-DD
   * @param {HTMLInputElement} timeInput - Input de hora
   */
  updateTimeMin(dateValue, timeInput) {
    if (!dateValue || !timeInput) return;

    try {
      const now = this.getEcuadorNow();
      const selectedDate = new Date(dateValue + 'T00:00:00');
      const today = new Date(now);
      today.setHours(0, 0, 0, 0);

      let minTotalMinutes = 0;

      if (selectedDate.getTime() === today.getTime()) {
        // Hoy: mínimo es 30 min desde ahora, redondeado al siguiente slot de 30
        minTotalMinutes = this.getMinSlotMinutesToday(now);
      }
      // Fecha futura: minTotalMinutes = 0 → todas las opciones habilitadas

      Array.from(timeInput.options || []).forEach(option => {
        if (!option.value) return; // opción placeholder en blanco: mantiene su propio disabled
        const [oh, om] = option.value.split(':').map(Number);
        option.disabled = oh * 60 + om < minTotalMinutes;
      });

      // Si el valor actual quedó deshabilitado, mover al primer slot disponible
      const current = timeInput.value;
      if (current) {
        const [ch, cm] = current.split(':').map(Number);
        if (ch * 60 + cm < minTotalMinutes) {
          const firstEnabled = Array.from(timeInput.options).find(o => !o.disabled);
          if (firstEnabled) {
            timeInput.value = firstEnabled.value;
            this.updateTimeDisplay(firstEnabled.value);
          }
        }
      }
    } catch (error) {
      console.error('Error al actualizar min de hora:', error);
    }
  }

  /**
   * Valida que la fecha y hora no sean en el pasado
   * y que haya al menos 30 minutos de antelación
   * @param {string} dateValue - Fecha en formato YYYY-MM-DD
   * @param {string} timeValue - Hora en formato HH:MM
   * @returns {boolean} - true si es válido, false si no
   */
  validateDateTime(dateValue, timeValue) {
    if (!dateValue || !timeValue) {
      return false;
    }

    try {
      const now = this.getEcuadorNow();
      const [year, month, day] = dateValue.split('-').map(Number);
      const [hours, minutes] = timeValue.split(':').map(Number);

      // Crear fecha/hora de recogida
      const pickupDateTime = new Date(year, month - 1, day, hours, minutes);

      // Verificar que no sea en el pasado
      if (pickupDateTime <= now) {
        return false;
      }

      // Verificar que haya al menos 30 minutos de antelación
      const thirtyMinutesFromNow = new Date(now.getTime() + 30 * 60 * 1000);
      if (pickupDateTime < thirtyMinutesFromNow) {
        return false;
      }

      // Verificar máximo de 15 días de anticipación
      const maxAllowed = new Date(now);
      maxAllowed.setHours(23, 59, 59, 999);
      maxAllowed.setDate(maxAllowed.getDate() + 15);
      if (pickupDateTime > maxAllowed) {
        return false;
      }

      // Recogida disponible las 24 horas: solo se valida que la hora exista
      if (hours < 0 || hours > 23) {
        return false;
      }

      // Verificar que los minutos sean 00 o 30
      if (minutes !== 0 && minutes !== 30) {
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error al validar fecha/hora:', error);
      return false;
    }
  }

  /**
   * Determina, campo por campo, qué mensaje de error mostrar (si corresponde)
   * para fecha y hora. No modifica las reglas de validateDateTime, solo las lee.
   * @param {string} dateValue
   * @param {string} timeValue
   * @returns {{date: string|null, time: string|null}}
   */
  getDateTimeFieldErrors(dateValue, timeValue) {
    const t = this.getTranslations();
    const errors = { date: null, time: null };

    if (!dateValue) {
      errors.date = t.errorDateRequired || 'Por favor, selecciona una fecha de recogida.';
    }
    if (!timeValue) {
      errors.time = t.errorTimeRequired || 'Por favor, selecciona una hora de recogida.';
    }
    if (!errors.date && !errors.time && !this.validateDateTime(dateValue, timeValue)) {
      errors.time =
        t.errorDatetimeInvalid ||
        'La fecha y hora de recogida deben ser al menos 30 minutos y no más de 15 días después de ahora. Por favor, selecciona una fecha y hora válidas.';
    }

    return errors;
  }

  /**
   * Resalta (o limpia) el campo indicado con borde rojo + mensaje debajo.
   * @param {'date'|'time'} field
   * @param {string|null} message - null limpia el error
   */
  setFieldError(field, message) {
    const fieldEl = this.popup?.querySelector(`.cs-location-selector-popup__${field}-field`);
    const messageEl = this.popup?.querySelector(`[data-field-message="${field}"]`);
    if (!fieldEl || !messageEl) return;

    if (message) {
      fieldEl.classList.add('has-error');
      messageEl.textContent = message;
      messageEl.classList.add('is-visible');
    } else {
      fieldEl.classList.remove('has-error');
      messageEl.textContent = '';
      messageEl.classList.remove('is-visible');
    }
  }

  /**
   * Aplica los errores de fecha/hora a sus campos y hace scroll al primero inválido.
   * @param {{date: string|null, time: string|null}} errors
   */
  showFieldErrors(errors) {
    this.setFieldError('date', errors.date);
    this.setFieldError('time', errors.time);

    const firstInvalidField = errors.date
      ? this.popup?.querySelector('.cs-location-selector-popup__date-field')
      : errors.time
        ? this.popup?.querySelector('.cs-location-selector-popup__time-field')
        : null;
    firstInvalidField?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  /**
   * Formatea una fecha en formato YYYY-MM-DD a texto legible
   * @param {string} dateValue - Fecha en formato YYYY-MM-DD
   * @returns {string} Fecha formateada (ej: "7 de Noviembre de 2025")
   */
  formatDateDisplay(dateValue) {
    if (!dateValue) return '';

    try {
      const date = new Date(dateValue + 'T00:00:00');
      if (isNaN(date.getTime())) {
        console.error('formatDateDisplay: fecha inválida', dateValue);
        return '';
      }

      const months = [
        'Enero',
        'Febrero',
        'Marzo',
        'Abril',
        'Mayo',
        'Junio',
        'Julio',
        'Agosto',
        'Septiembre',
        'Octubre',
        'Noviembre',
        'Diciembre',
      ];
      const day = date.getDate();
      const month = months[date.getMonth()];
      const year = date.getFullYear();
      return `${day} de ${month} de ${year}`;
    } catch (error) {
      console.error('formatDateDisplay: error al formatear', error);
      return '';
    }
  }

  /**
   * Fecha para mostrar en pantalla cuando el idioma de la tienda no es español
   * (ej: "15 de janeiro de 2026" en pt-BR). En español devuelve '' para que se use
   * formatDateDisplay, que es el texto que se guarda en pickup_date_display.
   * @param {string} dateValue - Fecha en formato YYYY-MM-DD
   * @returns {string}
   */
  formatDateForLocale(dateValue) {
    const lang = document.documentElement.lang || 'es';
    if (!dateValue || lang.startsWith('es')) return '';
    const date = new Date(dateValue + 'T00:00:00');
    if (isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat(lang, { day: 'numeric', month: 'long', year: 'numeric' }).format(date);
  }

  /**
   * Formatea una hora en formato HH:MM a texto legible
   * @param {string} timeValue - Hora en formato HH:MM
   * @returns {string} Hora formateada (ej: "11:35 am")
   */
  formatTimeDisplay(timeValue) {
    if (!timeValue) return '';

    try {
      const [hours, minutes] = timeValue.split(':');
      if (!hours || !minutes) {
        console.error('formatTimeDisplay: formato de hora inválido', timeValue);
        return '';
      }

      const hourNum = parseInt(hours, 10);
      if (isNaN(hourNum)) {
        console.error('formatTimeDisplay: hora inválida', hours);
        return '';
      }

      const ampm = hourNum >= 12 ? 'pm' : 'am';
      const displayHours = hourNum % 12 || 12;
      return `${displayHours}:${minutes} ${ampm}`;
    } catch (error) {
      console.error('formatTimeDisplay: error al formatear', error);
      return '';
    }
  }

  initRadioButtons() {
    const radioGroups = this.popup?.querySelectorAll('[name="department"], [name="terminal"]');
    if (!radioGroups) return;

    radioGroups.forEach((radio) => {
      radio.addEventListener('change', (e) => {
        const name = e.target.name;
        const allRadios = this.popup.querySelectorAll(`[name="${name}"]`);
        allRadios.forEach((r) => {
          if (r !== e.target) {
            r.checked = false;
          }
        });
      });
    });
  }

  /**
   * Actualiza el branch filter basado en los valores actuales del formulario
   * @param {boolean} updateUrl - Si debe actualizar la URL (default: false para cambios en tiempo real)
   */
  updateBranchFilterFromForm(updateUrl = false) {
    if (!this.form) return;

    const formData = new FormData(this.form);
    const department = formData.get('department');
    const terminal = formData.get('terminal');

    // Actualizar el header terminal cuando cambie la terminal
    if (terminal) {
      this.updateHeaderTerminal(terminal);
    }

    if (department && terminal) {
      const branchFilterValue = this.getBranchFilterValue(department, terminal);
      if (branchFilterValue) {
        this.updateBranchFilter(branchFilterValue, updateUrl);
      }
    }
  }

  /**
   * Inicializa el departamento desde localStorage
   */
  initDepartmentFromStorage() {
    if (!this.form) return;

    // Leer el catálogo guardado en localStorage
    const storedCatalog = localStorage.getItem('catalog');

    // Determinar qué radio button marcar
    let departmentValue = 'general'; // Por defecto

    // 'electronics' catalog is not available in this store — always fall back to 'general'
    if (storedCatalog === 'main') {
      departmentValue = 'general';
    }

    // Buscar y marcar el radio button correspondiente
    const departmentRadio = this.form.querySelector(`input[name="department"][value="${departmentValue}"]`);
    if (departmentRadio) {
      // Desmarcar todos los radios de department primero
      const allDepartmentRadios = this.form.querySelectorAll('input[name="department"]');
      allDepartmentRadios.forEach((radio) => {
        radio.checked = false;
      });

      // Marcar el radio correspondiente
      departmentRadio.checked = true;
    }
  }

  /**
   * Inicializa la terminal desde localStorage y actualiza el header
   */
  initTerminalFromStorage() {
    try {
      // Prioridad: localStorage -> formulario -> default
      let terminal = null;

      const stored = localStorage.getItem('locationData');
      if (stored) {
        const locationData = JSON.parse(stored);
        terminal = locationData?.terminal || null;
      }

      if (!terminal && this.form) {
        const formData = new FormData(this.form);
        terminal = formData.get('terminal');
      }

      if (!terminal) {
        terminal = 'terminal-1';
      }

      // Marcar el radio en el formulario según terminal
      if (this.form) {
        const radios = this.form.querySelectorAll('input[name="terminal"]');
        radios.forEach((r) => {
          r.checked = r.value === terminal;
        });
      }

      // Actualizar el header con el valor encontrado
      this.updateHeaderTerminal(terminal);
    } catch (error) {
      console.error('Error al leer locationData:', error);
      this.updateHeaderTerminal('terminal-1');
    }
  }

  /**
   * Actualiza el texto de la terminal en el botón del header
   * @param {string} terminalValue - Valor del terminal (terminal-1, terminal-2, etc.)
   */
  updateHeaderTerminal(terminalValue) {
    // Mapear el valor a texto legible
    const terminalText = this.getTerminalText(terminalValue);

    // Actualizar todos los botones del header (main y electronics)
    const terminalElements = document.querySelectorAll('.header__location-selector__terminal');
    terminalElements.forEach((element) => {
      element.textContent = terminalText;
    });
  }

  /**
   * Convierte el valor del terminal a texto legible
   * @param {string} terminalValue - Valor del terminal (terminal-1, terminal-2, etc.)
   * @returns {string} Texto legible (Terminal 1, Terminal 2, etc.)
   */
  getTerminalText(terminalValue) {
    const t = this.getTranslations();
    const mapping = {
      'terminal-1': t.popupTerminal1 || 'Tienda de Llegada',
      'terminal-2': t.popupTerminal2 || 'Tienda de Salida',
    };
    return mapping[terminalValue] || mapping['terminal-1'];
  }

  /**
   * Valores para los atributos del carrito (pickup_terminal, pickup_airport).
   * Siempre en español, sin importar el idioma de la tienda: checkout, órdenes
   * e integraciones leen estos textos. Las traducciones son solo para mostrar.
   */
  getTerminalAttributeText(terminalValue) {
    const mapping = {
      'terminal-1': 'Tienda de Llegada',
      'terminal-2': 'Tienda de Salida',
    };
    // Mismo fallback que getTerminalText: vacío o desconocido -> terminal-1
    return mapping[terminalValue] || mapping['terminal-1'];
  }

  getAirportAttributeText() {
    return 'Nuevo Aeropuerto Internacional Mariscal Sucre de Quito';
  }

  /** Nombre del aeropuerto traducido, solo para mostrar. */
  getAirportDisplayText() {
    return this.getTranslations().popupAirportName || this.getAirportAttributeText();
  }

  /** Nombre del país traducido, solo para mostrar (pickup_country se guarda sin traducir). */
  getCountryDisplayText(country) {
    if (!country) return '';
    const t = this.getTranslations();
    const mapping = {
      ecuador: t.popupCountryEcuador,
      panama: t.popupCountryPanama,
      'panamá': t.popupCountryPanama,
      colombia: t.popupCountryColombia,
    };
    return mapping[country.toLowerCase()] || country;
  }

  /**
   * Actualiza los atributos del carrito con la información de pickup
   * para que estén disponibles en el checkout UI extension.
   *
   * Los nombres de los atributos deben coincidir exactamente con los que espera
   * la extensión de checkout (pickup-info):
   * - pickup_country: País seleccionado (ej: "Panamá")
   * - pickup_airport: Nombre del aeropuerto (ej: "Tocumen International Airport")
   * - pickup_terminal: Terminal formateada (ej: "Terminal 1")
   * - pickup_date: Fecha formateada (ej: "15 de enero de 2025")
   * - pickup_time: Hora formateada (ej: "2:30 pm")
   *
   * @param {Object} locationData - Datos de ubicación de pickup
   */
  /**
   * Mapea la combinación de departamento y terminal al filter_value del branch filter
   * @param {string} department - 'general' o 'electronics'
   * @param {string} terminal - 'terminal-1' o 'terminal-2'
   * @returns {string} El filter_value correspondiente
   */
  getBranchConfig(department, terminal) {
    const mapping = {
      'general-terminal-1': {
        filter_value: 'Tienda de Llegada - Quito',
        location_id: 'gid://shopify/Location/84989411406',
      },
      'general-terminal-2': {
        filter_value: 'Tienda de Salida - Quito',
        location_id: 'gid://shopify/Location/85264793678',
      },
      // En Ecuador "electronics" es solo un cambio visual (metacampo del producto),
      // no una sucursal distinta: usa la misma ubicación que "general" según el
      // terminal elegido, para no mover al usuario de la tienda que seleccionó.
      'electronics-terminal-1': {
        filter_value: 'Tienda de Llegada - Quito',
        location_id: 'gid://shopify/Location/84989411406',
      },
      'electronics-terminal-2': {
        filter_value: 'Tienda de Salida - Quito',
        location_id: 'gid://shopify/Location/85264793678',
      },
    };

    const key = `${department}-${terminal}`;
    return mapping[key] || {};
  }

  getBranchFilterValue(department, terminal) {
    const config = this.getBranchConfig(department, terminal);
    return config.filter_value || '';
  }

  getLocationId(department, terminal) {
    const config = this.getBranchConfig(department, terminal);
    return config.location_id || '';
  }

  getLocationName(department, terminal) {
    const config = this.getBranchConfig(department, terminal);
    return config.filter_value || '';
  }

  /**
   * Actualiza el branch filter en localStorage y cart attributes
   * @param {string} filterValue - El filter_value del branch filter
   * @param {boolean} updateUrl - Si debe actualizar la URL (default: true)
   */
  async updateBranchFilter(filterValue, updateUrl = true) {
    if (!filterValue) return;

    const storageKey = 'branch_filter_selection';
    const cartAttrKey = 'branch_filter_selection';

    // Debug: Log antes de actualizar branch filter
    await this.debugCartAttributes('🔄 [CART DEBUG] updateBranchFilter - Antes de actualizar');

    // Guardar en localStorage
    localStorage.setItem(storageKey, filterValue);

    // Actualizar cart attribute preservando los atributos existentes
    try {
      // Obtener atributos existentes del carrito
      let existingAttributes = {};
      try {
        const cartResponse = await fetch('/cart.js');
        if (cartResponse.ok) {
          const cart = await cartResponse.json();
          if (cart.attributes) {
            existingAttributes = { ...cart.attributes };
          }
        }
      } catch (error) {
        console.warn('No se pudieron obtener los atributos existentes del carrito:', error);
      }

      // Combinar atributos existentes con el nuevo branch_filter_selection
      const allAttributes = {
        ...existingAttributes,
        [cartAttrKey]: filterValue,
      };

      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          attributes: allAttributes,
        }),
      });

      if (!response.ok) {
        console.error('❌ [CART DEBUG] Error al actualizar branch filter en carrito:', response.statusText);
        return;
      }

      const updatedCart = await response.json();
      console.log('✅ [CART DEBUG] Branch filter actualizado:', filterValue);
      console.log('  Response del servidor:', updatedCart);
      
      // Debug: Log después de actualizar
      await this.debugCartAttributes('🔄 [CART DEBUG] updateBranchFilter - Después de actualizar');

      // Si estamos en una colección o búsqueda y se solicita, actualizar la URL
      if (updateUrl) {
        const isCol = location.pathname.includes('/collections/') || location.pathname.includes('/search');
        if (isCol) {
          const urlParamName = 'filter.p.m.branch-filter.availability';
          const params = new URLSearchParams(location.search);
          const currentValue = params.get(urlParamName);

          // Solo redirigir si el valor cambió
          if (currentValue !== filterValue) {
            params.set(urlParamName, filterValue);
            location.href = location.pathname + '?' + params.toString();
          }
        }
      }
    } catch (error) {
      console.error('Error al actualizar branch filter:', error);
    }
  }

  async updateCartAttributes(locationData) {
    try {
      // Debug: Log antes de actualizar
      await this.debugCartAttributes('🔄 [CART DEBUG] updateCartAttributes - Antes de actualizar');
      
      // PRIMERO: Obtener los atributos existentes del carrito para preservarlos
      let existingAttributes = {};
      try {
        const cartResponse = await fetch('/cart.js');
        if (cartResponse.ok) {
          const cart = await cartResponse.json();
          // Preservar todos los atributos existentes (excepto los de pickup que vamos a actualizar)
          if (cart.attributes) {
            existingAttributes = { ...cart.attributes };
            console.log('📋 [CART DEBUG] Atributos existentes antes de actualizar:', existingAttributes);
          }
        }
      } catch (error) {
        console.warn('No se pudieron obtener los atributos existentes del carrito:', error);
        // Continuar con un objeto vacío si falla
      }

      // Terminal para el atributo: siempre en español (no usar traducciones)
      const terminalText = this.getTerminalAttributeText(locationData.terminal);

      // Tipo de catálogo: 'main' o 'electronics'. Fuente fiable para theme app extensions y checkout.
      const catalogType = (locationData.department === 'electronics') ? 'electronics' : 'main';

      // Preparar los nuevos atributos de pickup
      // Los valores deben ser strings según la API de Shopify
      const pickupAttributes = {
        // Tipo de catálogo (para extensiones y lógica externa: cart.attributes.catalog_type)
        catalog_type: String(catalogType),

        // Información básica de ubicación
        pickup_country: String(locationData.country || ''),
        pickup_airport: String(locationData.airport ? this.getAirportAttributeText() : ''),
        pickup_terminal: String(terminalText || ''),
        pickup_terminal_raw: String(locationData.terminal || ''), // Valor crudo (terminal-1, terminal-2)
        pickup_department: String(locationData.department || ''), // general o electronics
        
        // Información de fecha y hora (valores crudos y formateados)
        pickup_date: String(locationData.date || ''), // Valor crudo (YYYY-MM-DD)
        pickup_date_display: String(locationData.dateDisplay || ''), // Valor formateado (ej: "15 de enero de 2025")
        pickup_time: String(locationData.time || ''), // Valor crudo (HH:MM)
        pickup_time_display: String(locationData.timeDisplay || ''), // Valor formateado (ej: "2:30 pm")
        
        // Información de ubicación de Shopify
        pickup_location_id: String(locationData.location_id || ''),
        pickup_location_name: String(locationData.location_name || ''),
        
        // Timestamp de cuando se estableció la información
        pickup_timestamp: String(locationData.timestamp || new Date().toISOString()),
      };

      // SINCRONIZAR: Asegurar que todos los atributos de ubicación estén sincronizados
      // pickup_location_id es la fuente de verdad más confiable (ID único de Shopify)
      // pickup_location_name y branch_filter_selection deben coincidir con el location_id
      if (pickupAttributes.pickup_location_id && pickupAttributes.pickup_location_name) {
        // Sincronizar branch_filter_selection con pickup_location_name
        pickupAttributes.branch_filter_selection = pickupAttributes.pickup_location_name;
        
        // Validar que el location_id corresponde al location_name usando el mapping
        const department = locationData.department || '';
        const terminal = locationData.terminal || '';
        const expectedLocationId = this.getLocationId(department, terminal);
        const expectedLocationName = this.getLocationName(department, terminal);
        
        // Si hay inconsistencia, corregir usando el mapping como fuente de verdad
        if (expectedLocationId && expectedLocationId !== pickupAttributes.pickup_location_id) {
          console.warn('⚠️ Inconsistencia detectada en pickup_location_id. Corrigiendo...');
          pickupAttributes.pickup_location_id = expectedLocationId;
        }
        if (expectedLocationName && expectedLocationName !== pickupAttributes.pickup_location_name) {
          console.warn('⚠️ Inconsistencia detectada en pickup_location_name. Corrigiendo...');
          pickupAttributes.pickup_location_name = expectedLocationName;
          pickupAttributes.branch_filter_selection = expectedLocationName;
        }
      }

      // COMBINAR: Mezclar atributos existentes con los nuevos atributos de pickup
      // Los atributos de pickup sobrescribirán cualquier valor previo de pickup_*
      // pero se preservan todos los demás atributos existentes
      const allAttributes = {
        ...existingAttributes,
        ...pickupAttributes,
      };

      // Debug: Log antes de enviar
      console.log('📤 [CART DEBUG] Enviando atributos al carrito:');
      console.log('  - Atributos nuevos de pickup:', pickupAttributes);
      console.log('  - Todos los atributos combinados:', allAttributes);

      // Actualizar el carrito usando la API de Shopify con TODOS los atributos
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          attributes: allAttributes,
        }),
      });

      if (!response.ok) {
        console.error('❌ [CART DEBUG] Error al actualizar atributos del carrito:', response.statusText);
        console.error('  Response status:', response.status);
        console.error('  Response statusText:', response.statusText);
        return;
      }

      const cartData = await response.json();
      console.log('✅ [CART DEBUG] Atributos de pickup guardados en el carrito');
      console.log('  Response del servidor:', cartData);
      
      // Debug: Verificar que los atributos se guardaron correctamente
      if (cartData.attributes) {
        console.log('📥 [CART DEBUG] Atributos confirmados en el carrito:');
        console.table(cartData.attributes);
        
        // Verificar atributos de pickup específicamente
        const pickupAttrsInResponse = {};
        Object.keys(cartData.attributes).forEach(key => {
          if (key.startsWith('pickup_') || key === 'branch_filter_selection') {
            pickupAttrsInResponse[key] = cartData.attributes[key];
          }
        });
        
        if (Object.keys(pickupAttrsInResponse).length > 0) {
          console.log('🎯 [CART DEBUG] Atributos de pickup confirmados:');
          console.table(pickupAttrsInResponse);
        } else {
          console.warn('⚠️ [CART DEBUG] No se encontraron atributos de pickup en la respuesta');
        }
      } else {
        console.warn('⚠️ [CART DEBUG] La respuesta no incluye atributos');
      }
      
      // Debug: Log después de actualizar (verificar en el servidor)
      await this.debugCartAttributes('🔄 [CART DEBUG] updateCartAttributes - Después de actualizar (verificación)');
      
      return cartData;
    } catch (error) {
      console.error('Error al actualizar atributos del carrito:', error);
      throw error;
    }
  }

  /**
   * Espera a que el cart attribute se haya actualizado en el servidor
   * @param {string} attributeName - Nombre del atributo a verificar (ej: 'pickup_location_name')
   * @param {string} expectedValue - Valor esperado
   * @param {number} maxAttempts - Número máximo de intentos (default: 10)
   * @param {number} delayMs - Delay entre intentos en ms (default: 300)
   * @returns {Promise<boolean>} - true si el atributo se actualizó, false si no
   */
  async waitForCartAttribute(attributeName, expectedValue, maxAttempts = 10, delayMs = 300) {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const cartResponse = await fetch('/cart.js');
        if (!cartResponse.ok) {
          await new Promise((resolve) => setTimeout(resolve, delayMs));
          continue;
        }

        const cart = await cartResponse.json();
        const attributeValue = cart.attributes?.[attributeName] || '';

        if (attributeValue === expectedValue) {
          return true; // El atributo se actualizó correctamente
        }

        // Esperar antes del siguiente intento
        await new Promise((resolve) => setTimeout(resolve, delayMs));
      } catch (error) {
        console.error(`Error verificando cart attribute (intento ${attempt + 1}):`, error);
        await new Promise((resolve) => setTimeout(resolve, delayMs));
      }
    }

    return false; // No se pudo verificar el cambio después de todos los intentos
  }

  /**
   * Determina la terminal opuesta a la actual y ejecuta el cambio
   * simulando el submit del formulario (con todos sus checks y alertas).
   */
  async switchToOtherTerminal() {
    // Leer terminal actual desde localStorage
    let currentTerminal = 'terminal-1';
    try {
      const stored = localStorage.getItem('locationData');
      if (stored) {
        currentTerminal = JSON.parse(stored)?.terminal || 'terminal-1';
      }
    } catch (e) {
      console.error('Error leyendo locationData:', e);
    }

    const otherTerminal = currentTerminal === 'terminal-1' ? 'terminal-2' : 'terminal-1';

    // Seleccionar la terminal opuesta en el formulario
    if (this.form) {
      const radios = this.form.querySelectorAll('input[name="terminal"]');
      radios.forEach((r) => {
        r.checked = r.value === otherTerminal;
      });
    }

    // Delegar al submit normal para que corran todos los checks y alertas
    await this.handleFormSubmit();
  }

  attachEventListeners() {
    // Abrir popup desde botones trigger
    this.triggers.forEach((trigger) => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        if (trigger.dataset.action === 'switch-terminal') {
          this.switchToOtherTerminal();
        } else {
          this.open();
        }
      });
    });

    // Cerrar popup desde overlay
    if (this.overlay) {
      this.overlay.addEventListener('click', () => {
        this.close();
      });
    }

    // Cerrar popup desde botones cerrar
    if (this.closeButtons) {
      this.closeButtons.forEach((button) => {
        button.addEventListener('click', () => {
          this.close();
        });
      });
    }

    // Manejar submit del formulario
    if (this.form) {
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.handleFormSubmit();
      });
    }

    // Manejar botones de "cómo llegar"
    const directionsButtons = this.popup?.querySelectorAll('.cs-location-selector-popup__directions-btn');
    if (directionsButtons) {
      directionsButtons.forEach((button) => {
        button.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.handleDirectionsClick(button);
        });
      });
    }

    // Cerrar con tecla ESC
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) {
        this.close();
      }
    });
  }

  open() {
    if (!this.popup) return;

    // Actualizar la selección del departamento desde localStorage antes de abrir
    this.initDepartmentFromStorage();

    // Cargar fecha y hora desde localStorage antes de abrir
    this.initDateTimeFromStorage();

    // Limpiar cualquier error de campo que haya quedado de una apertura anterior
    this.setFieldError('date', null);
    this.setFieldError('time', null);

    this.popup.style.display = 'block';
    document.body.style.overflow = 'hidden';

    // Usar requestAnimationFrame para asegurar que las transiciones se ejecuten
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.popup.classList.add('is-active');
      });
    });
  }

  close() {
    if (!this.popup) return;
    this.popup.classList.remove('is-active');
    document.body.style.overflow = '';

    // Esperar a que termine la animación antes de ocultar
    setTimeout(() => {
      if (!this.popup.classList.contains('is-active')) {
        this.popup.style.display = '';
      }
    }, 300);
  }

  isOpen() {
    return this.popup?.classList.contains('is-active') || false;
  }

  async handleFormSubmit() {
    if (!this.form) return;

    // Debug: Log antes de procesar el formulario
    await this.debugCartAttributes('🔄 [CART DEBUG] handleFormSubmit - Antes de procesar formulario');

    const formData = new FormData(this.form);
    // Buscar inputs dentro del popup específicamente
    const dateInput = this.popup?.querySelector('#pickup-date-input') || document.getElementById('pickup-date-input');
    const timeInput = this.popup?.querySelector('#pickup-time-input') || document.getElementById('pickup-time-input');

    // Obtener el nombre del aeropuerto desde el texto mostrado
    // Aeropuerto en español fijo: se guarda en localStorage y en pickup_airport
    const airportText = this.getAirportAttributeText();

    // Calcular dateDisplay y timeDisplay desde los valores del input
    // en lugar de leer el texto del DOM que puede estar desactualizado
    const dateValue = dateInput?.value || '';
    const timeValue = timeInput?.value || '';

    // VALIDAR fecha y hora antes de continuar (resalta el/los campo(s) faltante(s) o inválido(s))
    const dateTimeErrors = this.getDateTimeFieldErrors(dateValue, timeValue);
    if (dateTimeErrors.date || dateTimeErrors.time) {
      this.showFieldErrors(dateTimeErrors);
      return;
    }
    this.setFieldError('date', null);
    this.setFieldError('time', null);

    let dateDisplay = '';
    let timeDisplay = '';

    if (dateValue) {
      dateDisplay = this.formatDateDisplay(dateValue);
    }

    if (timeValue) {
      timeDisplay = this.formatTimeDisplay(timeValue);
    }

    // Mapear el valor del país a su nombre correcto con mayúscula y tilde
    const countryValue = formData.get('country');
    let countryDisplay = countryValue;

    // Mapear valores a sus nombres correctos
    if (countryValue === 'panama') {
      countryDisplay = 'Panamá';
    }

    const department = formData.get('department');
    const terminal = formData.get('terminal');
    const locationId = this.getLocationId(department, terminal);
    const locationName = this.getLocationName(department, terminal);

    const locationData = {
      country: countryDisplay,
      airport: airportText || '',
      department: department, // Guardar department para poder restaurarlo
      terminal: terminal,
      date: dateValue,
      time: timeValue,
      dateDisplay: dateDisplay,
      timeDisplay: timeDisplay,
      location_id: locationId,
      location_name: locationName,
      timestamp: new Date().toISOString(),
    };

    console.log('Location data:', locationData);

    // Verificar disponibilidad de items del carrito antes de confirmar
    if (locationName) {
      const availabilityCheck = await this.checkCartItemsAvailability(locationName);

      if (availabilityCheck.unavailable.length > 0) {
        // Hay items no disponibles, mostrar advertencia
        const userChoice = await this.showAvailabilityWarning(
          availabilityCheck.unavailable,
          locationName,
          availabilityCheck.previousLocation,
        );

        if (userChoice === null) {
          // Usuario canceló, no hacer nada
          return;
        } else if (userChoice === false) {
          // Usuario quiere volver a la sucursal anterior
          if (availabilityCheck.previousLocation) {
            // Restaurar la selección anterior
            const previousLocationData = localStorage.getItem('locationData');
            if (previousLocationData) {
              const previous = JSON.parse(previousLocationData);
              // Restaurar los valores del formulario
              if (previous.terminal) {
                const terminalRadio = this.form.querySelector(`input[name="terminal"][value="${previous.terminal}"]`);
                if (terminalRadio) {
                  terminalRadio.checked = true;
                  this.updateHeaderTerminal(previous.terminal);
                }
              }
              if (previous.department) {
                const departmentSelect = this.form.querySelector('select[name="department"]');
                if (departmentSelect) {
                  departmentSelect.value = previous.department;
                }
              }
            }
          }
          return;
        } else if (userChoice === true) {
          // Usuario quiere eliminar items no disponibles
          // PRIMERO: Guardar la nueva location en localStorage
          localStorage.setItem('locationData', JSON.stringify(locationData));
          if (locationId) {
            localStorage.setItem('pickup_location_id', locationId);
          }

          let catalogValue = 'main';
          if (department === 'electronics') {
            catalogValue = 'electronics';
          } else if (department === 'general') {
            catalogValue = 'main';
          }
          const previousCatalog = localStorage.getItem('catalog') || 'main';
          localStorage.setItem('catalog', catalogValue);

          // CRÍTICO: Eliminar items no disponibles ANTES de cualquier redirect.
          // Si redirigimos por cambio de catálogo antes, la página se va y nunca se borran los ítems.
          await this.removeUnavailableItems(availabilityCheck.unavailable, true);

          // Si cambió el catálogo, actualizar atributos del carrito y luego redirigir al homepage
          if (previousCatalog !== catalogValue) {
            await this.updateCartAttributes(locationData);
            const localeMatch = window.location.pathname.match(/^(\/[a-z]{2})(\/|$)/);
            const root = localeMatch ? localeMatch[1] : '';
            window.location.href = catalogValue === 'electronics' ? `${root}/?department=electronics` : `${root}/`;
            return;
          }

          // CUARTO: Actualizar cart attributes
          await this.updateCartAttributes(locationData);

          // QUINTO: Esperar a que el cart attribute se haya actualizado en el servidor
          const locationName = locationData.location_name || '';
          if (locationName) {
            await this.waitForCartAttribute('pickup_location_name', locationName);
          }

          // QUINTO: Actualizar el formulario visualmente
          if (this.form) {
            // Actualizar department
            const departmentRadio = this.form.querySelector(`input[name="department"][value="${department}"]`);
            if (departmentRadio) {
              const allDepartmentRadios = this.form.querySelectorAll('input[name="department"]');
              allDepartmentRadios.forEach((radio) => {
                radio.checked = false;
              });
              departmentRadio.checked = true;
            }

            // Actualizar terminal
            const terminalRadios = this.form.querySelectorAll('input[name="terminal"]');
            terminalRadios.forEach((r) => {
              r.checked = r.value === terminal;
            });
          }

          // SEXTO: Actualizar header terminal
          this.updateHeaderTerminal(terminal);

          // SÉPTIMO: Aplicar el modo de catálogo
          if (typeof window.applyCatalogMode === 'function') {
            window.applyCatalogMode(catalogValue, true);
          } else {
            setTimeout(() => {
              if (typeof window.applyCatalogMode === 'function') {
                window.applyCatalogMode(catalogValue, true);
              }
            }, 100);
          }

          // OCTAVO: Actualizar URL de colecciones si es necesario
          const filterValue = this.getBranchFilterValue(department, terminal);
          if (filterValue) {
            const isCol = location.pathname.includes('/collections/') || location.pathname.includes('/search');
            if (isCol) {
              const urlParamName = 'filter.p.m.branch-filter.availability';
              const params = new URLSearchParams(location.search);
              params.set(urlParamName, filterValue);
              window.history.replaceState({}, '', location.pathname + '?' + params.toString());
            }
          }

          // NOVENO: Disparar evento locationChanged
          window.dispatchEvent(
            new CustomEvent('locationChanged', {
              detail: { locationData: locationData },
            }),
          );

          // Redirigir al homepage respetando el prefijo de locale (/es, /en, etc.)
          const localeMatch = window.location.pathname.match(/^(\/[a-z]{2})(\/|$)/);
          window.location.href = localeMatch ? localeMatch[1] : '/';
          return;
        }
      }
    }

    // Guardar toda la información en localStorage como JSON
    // Esto permite que otros scripts del theme puedan acceder a los datos
    localStorage.setItem('locationData', JSON.stringify(locationData));

    // Guardar pickup_location_id directamente en localStorage para el branch filter
    if (locationId) {
      localStorage.setItem('pickup_location_id', locationId);
    }

    // Actualizar atributos del carrito para que lleguen al checkout UI extension
    // Los atributos se guardan con los nombres: pickup_country, pickup_airport,
    // pickup_terminal, pickup_date, pickup_time
    console.log('📝 [CART DEBUG] handleFormSubmit - locationData que se enviará:', locationData);
    await this.updateCartAttributes(locationData);
    
    // Debug: Log después de actualizar atributos
    await this.debugCartAttributes('🔄 [CART DEBUG] handleFormSubmit - Después de updateCartAttributes');

    // Disparar evento personalizado para notificar cambios en la sucursal
    // Hacerlo después de actualizar los cart attributes
    setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent('locationChanged', {
          detail: { locationData: locationData },
        }),
      );
    }, 100);

    // Actualizar el carrito instantáneamente sin recargar la página
    if (typeof window.cartPickupData !== 'undefined' && window.cartPickupData) {
      window.cartPickupData.loadPickupData();
    } else {
      // Si no está disponible, buscar elementos del carrito directamente
      const cartPickupInfo = document.querySelector('.cart__pickup-info');
      if (cartPickupInfo) {
        const countryEl = cartPickupInfo.querySelector('#pickup-country');
        const airportEl = cartPickupInfo.querySelector('#pickup-airport');
        const terminalEl = cartPickupInfo.querySelector('#pickup-terminal');
        const dateEl = cartPickupInfo.querySelector('#pickup-date');
        const timeEl = cartPickupInfo.querySelector('#pickup-time');

        if (countryEl) {
          countryEl.textContent = this.getCountryDisplayText(locationData.country) || '-';
        }
        if (airportEl) {
          airportEl.textContent = locationData.airport ? this.getAirportDisplayText() : '-';
        }
        if (terminalEl) {
          // Convertir terminal-1 a Terminal 1
          const terminalText = this.getTerminalText(locationData.terminal);
          terminalEl.textContent = terminalText || '-';
        }
        if (dateEl) {
          dateEl.textContent =
            this.formatDateForLocale(locationData.date) || locationData.dateDisplay || locationData.date || '-';
        }
        if (timeEl) {
          timeEl.textContent = locationData.timeDisplay || locationData.time || '-';
        }
      }
    }

    // Actualizar la terminal en el header
    this.updateHeaderTerminal(locationData.terminal);

    // Guardar el catálogo en localStorage
    let catalogValue = 'main'; // Por defecto

    if (department === 'electronics') {
      catalogValue = 'electronics';
    } else if (department === 'general') {
      catalogValue = 'main';
    }

    const previousCatalog = localStorage.getItem('catalog') || 'main';
    localStorage.setItem('catalog', catalogValue);

    // Si cambió el catálogo (no solo terminal/sucursal), redirigir al homepage
    if (previousCatalog !== catalogValue) {
      const localeMatch = window.location.pathname.match(/^(\/[a-z]{2})(\/|$)/);
      const root = localeMatch ? localeMatch[1] : '';
      window.location.href = catalogValue === 'electronics' ? `${root}/?department=electronics` : `${root}/`;
      return;
    }

    // Actualizar URL si estamos en una página de colección o búsqueda
    const filterValue = this.getBranchFilterValue(department, terminal);
    if (filterValue) {
      const isCol = location.pathname.includes('/collections/') || location.pathname.includes('/search');
      if (isCol) {
        const urlParamName = 'filter.p.m.branch-filter.availability';
        const params = new URLSearchParams(location.search);
        const currentValue = params.get(urlParamName);

        // Si el valor cambió, actualizar la URL
        if (currentValue !== filterValue) {
          params.set(urlParamName, filterValue);
          // Usar replace para evitar agregar al historial
          location.replace(location.pathname + '?' + params.toString());
          return; // Salir temprano porque se va a recargar la página
        }
      }
    }

    // Aplicar el modo de catálogo en todas las páginas
    // Esto cambiará headers, esquemas de color y secciones (si está en homepage)
    if (typeof window.applyCatalogMode === 'function') {
      window.applyCatalogMode(catalogValue, true);
    } else {
      // Si la función no está disponible aún, esperar un poco y reintentar
      setTimeout(() => {
        if (typeof window.applyCatalogMode === 'function') {
          window.applyCatalogMode(catalogValue, true);
        } else {
          console.warn('applyCatalogMode no está disponible');
        }
      }, 100);
    }

    // Cerrar el popup
    this.close();
  }

  handleDirectionsClick(button) {
    const url = button.getAttribute('data-url');
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }

  /**
   * Verifica la disponibilidad de los items del carrito en la nueva sucursal
   * @param {string} newLocationName - Nombre de la nueva sucursal seleccionada
   * @returns {Promise<{unavailable: Array, previousLocation: string}>}
   */
  async checkCartItemsAvailability(newLocationName) {
    try {
      // Obtener el carrito actual
      const cartResponse = await fetch('/cart.js');
      if (!cartResponse.ok) {
        return { unavailable: [], previousLocation: null };
      }

      const cart = await cartResponse.json();
      if (!cart.items || cart.items.length === 0) {
        return { unavailable: [], previousLocation: null };
      }

      // Obtener la sucursal anterior desde localStorage
      const previousLocationData = localStorage.getItem('locationData');
      const previousLocation = previousLocationData ? JSON.parse(previousLocationData).location_name : null;

      // Obtener los metafields de todas las variantes del carrito
      const variantIds = cart.items.map((item) => item.variant_id);
      const unavailableItems = [];

      // Para cada item del carrito, verificar disponibilidad (índice 1-based para /cart/change.js)
      for (let i = 0; i < cart.items.length; i++) {
        const item = cart.items[i];
        const variantId = item.variant_id;
        const metadata = this.getVariantMetadata(variantId);

        // Si es hijo de un bundle, se omite — lo cubre la validación del padre
        if (metadata?.is_bundle_child) continue;

        // Detectar bundle: por flag del script pre-cargado o por fallback en JS
        const isBundle = metadata?.is_bundle_parent
          || item.handle?.startsWith('attenza-bundle')
          || !!item.properties?._bundle_children;

        let isAvailable = false;

        if (isBundle) {
          // Obtener children del script pre-cargado
          let children = metadata?.bundle_children || [];

          // Leer hijos directamente desde _bundle_children_info escrito por la app de bundles.
          // Es la única fuente confiable: el Cart Transform ya leyó branch-filter.availability.
          if (children.length === 0 && item.properties?._bundle_children_info) {
            try {
              const info = JSON.parse(item.properties._bundle_children_info);
              children = info.map((c) => ({
                variant_id: parseInt((c.variantId || '').split('/').pop(), 10),
                availability: 'availability' in c ? c.availability : undefined,
              }));
            } catch (e) {
              console.error('Error parseando _bundle_children_info:', e);
            }
          }

          if (children.length === 0) {
            // Sin datos de hijos → permitir
            isAvailable = true;
          } else {
            isAvailable = children.every((child) => {
              // La app no incluyó availability (bundle creado antes del deploy) → permitir
              if (child.availability === undefined) return true;
              // null = sin restricción → disponible en todas partes
              const childAvailability = this.parseAvailabilityValue(child.availability);
              if (childAvailability.length === 0) return true;
              return childAvailability.some((loc) => loc.trim() === newLocationName.trim());
            });
          }
        } else {
          // Producto normal: verificar su propio metafield
          const availabilityList = this.getVariantAvailability(variantId);
          isAvailable = availabilityList.length > 0 && availabilityList.some((loc) => loc.trim() === newLocationName.trim());
        }

        if (!isAvailable) {
          unavailableItems.push({
            id: item.id,
            key: item.key,
            line: i + 1,
            variant_id: variantId,
            title: item.product_title,
            variant_title: item.variant_title || 'Default',
            quantity: item.quantity,
          });
        }
      }

      return {
        unavailable: unavailableItems,
        previousLocation: previousLocation,
      };
    } catch (error) {
      console.error('Error al verificar disponibilidad del carrito:', error);
      return { unavailable: [], previousLocation: null };
    }
  }

  /**
   * Parsea un valor de availability (array o string separado por comas) a string[]
   * @param {any} value
   * @returns {string[]}
   */
  parseAvailabilityValue(value) {
    if (!value) return [];
    if (Array.isArray(value)) return value.map((v) => String(v).trim()).filter((s) => s.length > 0);
    if (typeof value === 'string') {
      // Intentar parsear como JSON primero (ej: "[]" o '["Tienda A","Tienda B"]')
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) return parsed.map((v) => String(v).trim()).filter((s) => s.length > 0);
      } catch {}
      // Fallback: string separado por comas
      return value.split(',').map((s) => s.trim()).filter((s) => s.length > 0);
    }
    return [];
  }

  /**
   * Obtiene los metafields de una variante desde el script pre-cargado
   * @param {number} variantId - ID de la variante
   * @returns {string[]}
   */
  getVariantAvailability(variantId) {
    try {
      const metafieldsScript = document.getElementById('cart-variant-metafields');
      if (!metafieldsScript) return [];
      const metafields = JSON.parse(metafieldsScript.textContent);
      const value = metafields[variantId]?.['branch-filter']?.['availability']?.value;
      return this.parseAvailabilityValue(value);
    } catch (error) {
      console.error(`Error al obtener metafield de variante ${variantId}:`, error);
      return [];
    }
  }

  /**
   * Obtiene los datos completos de un item desde el script pre-cargado
   * @param {number} variantId
   * @returns {object|null}
   */
  getVariantMetadata(variantId) {
    try {
      const metafieldsScript = document.getElementById('cart-variant-metafields');
      if (!metafieldsScript) return null;
      const metafields = JSON.parse(metafieldsScript.textContent);
      return metafields[variantId] || null;
    } catch {
      return null;
    }
  }

  /**
   * Muestra un modal de advertencia cuando hay items no disponibles
   * @param {Array} unavailableItems - Lista de items no disponibles
   * @param {string} newLocationName - Nombre de la nueva sucursal
   * @param {string} previousLocationName - Nombre de la sucursal anterior
   * @returns {Promise<boolean>} - true si el usuario quiere continuar, false si quiere volver
   */
  async showAvailabilityWarning(unavailableItems, newLocationName, previousLocationName) {
    return new Promise((resolve) => {
      // Crear el modal si no existe
      let modal = document.getElementById('location-availability-warning-modal');
      if (!modal) {
        const t = this.getTranslations();
        modal = document.createElement('div');
        modal.id = 'location-availability-warning-modal';
        modal.className = 'location-availability-warning-modal';
        modal.innerHTML = `
          <div class="location-availability-warning-modal__overlay"></div>
          <div class="location-availability-warning-modal__content">
            <h2 class="location-availability-warning-modal__title">${t.availabilityWarningTitle || '¡Advertencia!'}</h2>
            <p class="location-availability-warning-modal__subtitle" style="color: rgba(var(--color-foreground), 0.6); font-size: 1.8rem; margin-top: 0;">${t.availabilityWarningSubtitle || 'Estás cambiando de tienda'}</p>
            <p class="location-availability-warning-modal__message">
              ${t.availabilityWarningMessage || 'Algunos productos en tu carrito no están disponibles en la tienda a la que estás cambiando.'}
            </p>
            <div class="location-availability-warning-modal__items">
              <ul class="location-availability-warning-modal__list">
                ${unavailableItems
                  .map(
                    (item) => `
                  <li>${item.title}${item.variant_title !== 'Default' ? ` - ${item.variant_title}` : ''} (${t.availabilityWarningQuantityLabel || 'Cantidad'}: ${item.quantity})</li>
                `,
                  )
                  .join('')}
              </ul>
            </div>
            <div class="location-availability-warning-modal__actions">
              <button class="button button--secondary" data-action="revert">
                ${t.availabilityWarningRevertButton || 'Mantenerme en la tienda actual'}
              </button>
              <button class="button button--primary" data-action="remove">
                ${t.availabilityWarningRemoveButton || 'Eliminar productos no disponibles y cambiar de tienda'}
              </button>
            </div>
          </div>
        `;
        document.body.appendChild(modal);

        // Agregar estilos básicos si no existen
        if (!document.getElementById('location-availability-warning-modal-styles')) {
          const styles = document.createElement('style');
          styles.id = 'location-availability-warning-modal-styles';
          styles.textContent = `
            .location-availability-warning-modal {
              position: fixed;
              top: 0;
              left: 0;
              width: 100%;
              height: 100%;
              z-index: 10000;
              display: flex;
              align-items: center;
              justify-content: center;
            }
            .location-availability-warning-modal__overlay {
              position: absolute;
              top: 0;
              left: 0;
              width: 100%;
              height: 100%;
              background-color: rgba(0, 0, 0, 0.3);
              opacity: 1;
              z-index: 0;
            }
            .location-availability-warning-modal__content {
              position: relative;
              background: white;
              padding: 2rem;
              border-radius: 8px;
              max-width: 500px;
              width: 90%;
              max-height: 80vh;
              overflow-y: auto;
              z-index: 1;
              box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
            }
              @media screen and (min-width: 750px) {
                .location-availability-warning-modal__content {
                  padding: 4rem;
                }
              }
            .location-availability-warning-modal__title {
              margin: 0 0 1rem 0;
              font-weight: bold;
            }
            .location-availability-warning-modal__message {
              margin: 0 0 1rem 0;
            }
            .location-availability-warning-modal__list {
              margin: 1rem 0;
              padding-left: 1.5rem;
            }
            .location-availability-warning-modal__list li {
              margin: 0.5rem 0;
            }
            .location-availability-warning-modal__actions {
              display: flex;
              flex-direction: column;
              gap: 0.75rem;
              margin-top: 1.5rem;
            }
            .location-availability-warning-modal__btn {
              padding: 0.75rem 1.5rem;
              border: none;
              border-radius: 4px;
              cursor: pointer;
              font-size: 1rem;
              font-weight: 500;
            }
            .location-availability-warning-modal__btn--primary {
              background: #d32f2f;
              color: white;
            }
            .location-availability-warning-modal__btn--primary:hover {
              background: #b71c1c;
            }
            .location-availability-warning-modal__btn--secondary {
              background: #1976d2;
              color: white;
            }
            .location-availability-warning-modal__btn--secondary:hover {
              background: #1565c0;
            }
            .location-availability-warning-modal__btn--cancel {
              background: #e0e0e0;
              color: #333;
            }
            .location-availability-warning-modal__btn--cancel:hover {
              background: #bdbdbd;
            }
          `;
          document.head.appendChild(styles);
        }
      }

      // Asegurar que el overlay esté presente
      let overlay = modal.querySelector('.location-availability-warning-modal__overlay');
      if (!overlay) {
        overlay = document.createElement('div');
        overlay.className = 'location-availability-warning-modal__overlay';
        modal.insertBefore(overlay, modal.firstChild);
      }

      // Actualizar el contenido del modal
      const t = this.getTranslations();
      const titleEl = modal.querySelector('.location-availability-warning-modal__title');
      const messageEl = modal.querySelector('.location-availability-warning-modal__message');
      const listEl = modal.querySelector('.location-availability-warning-modal__list');
      const revertBtn = modal.querySelector('[data-action="revert"]');
      const removeBtn = modal.querySelector('[data-action="remove"]');
      const cancelBtn = modal.querySelector('[data-action="cancel"]');

      if (titleEl) {
        titleEl.textContent = t.availabilityWarningTitle || '¡Advertencia!';
      }
      if (messageEl) {
        messageEl.innerHTML =
          t.availabilityWarningMessage ||
          'Algunos productos en tu carrito no están disponibles en la tienda a la que estás cambiando.';
      }
      if (listEl) {
        listEl.innerHTML = unavailableItems
          .map(
            (item) => `
          <li>${item.title}${item.variant_title !== 'Default' ? ` - ${item.variant_title}` : ''} (${t.availabilityWarningQuantityLabel || 'Cantidad'}: ${item.quantity})</li>
        `,
          )
          .join('');
      }

      if (revertBtn) {
        revertBtn.textContent = t.availabilityWarningRevertButton || 'Mantenerme en la tienda actual';
        revertBtn.style.display = 'block';
      }
      if (removeBtn) {
        removeBtn.textContent =
          t.availabilityWarningRemoveButton || 'Eliminar productos no disponibles y cambiar de tienda';
      }

      // Mostrar el modal con overlay visible
      modal.style.display = 'flex';
      if (overlay) {
        overlay.style.display = 'block';
      }

      // Manejar clics en los botones (referencias estables para poder remover después)
      const handleRemove = () => {
        modal.style.display = 'none';
        resolve(true);
      };
      const handleRevert = () => {
        modal.style.display = 'none';
        resolve(false);
      };
      const handleCancel = () => {
        modal.style.display = 'none';
        resolve(null);
      };

      if (removeBtn) {
        removeBtn.onclick = handleRemove;
      }
      if (revertBtn) {
        revertBtn.onclick = handleRevert;
      }
      if (cancelBtn) {
        cancelBtn.onclick = handleCancel;
      }
      const overlayEl = modal.querySelector('.location-availability-warning-modal__overlay');
      if (overlayEl) {
        overlayEl.onclick = handleCancel;
      }
    });
  }

  /**
   * Elimina items no disponibles del carrito vía /cart/change.js (id = line item key, quantity = 0).
   * @param {Array} unavailableItems - Lista de items a eliminar (debe incluir key)
   * @param {boolean} skipReload - Si true, no recarga la página al terminar (el llamador recargará)
   */
  async removeUnavailableItems(unavailableItems, skipReload = false) {
    if (!unavailableItems || unavailableItems.length === 0) {
      if (!skipReload) location.reload();
      return;
    }

    try {
      const sorted = [...unavailableItems].sort((a, b) => (b.line || 0) - (a.line || 0));

      for (const item of sorted) {
        const lineItemKey = item.key || (item.id != null ? String(item.id) : null);
        if (!lineItemKey) continue;

        const body = { id: lineItemKey, quantity: 0 };
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });

        const responseText = await response.text();
        if (!response.ok) continue;

        await new Promise((r) => setTimeout(r, 200));
      }

      if (!skipReload) {
        await new Promise((r) => setTimeout(r, 400));
        location.reload();
      }
    } catch (error) {
      if (!skipReload) {
        const t = this.getTranslations();
        alert(t.errorRemoveItemsReload || 'Hubo un error al eliminar algunos productos. La página se recargará.');
        location.reload();
      }
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.locationSelectorPopup = new LocationSelectorPopup();
  });
} else {
  window.locationSelectorPopup = new LocationSelectorPopup();
}
